import { useEffect, useMemo, useState } from "react";
import { LayoutDashboard, AlertCircle, Settings2, Save, X } from "lucide-react";
import { supabase } from "../lib/supabase";
import type { NiaraDay } from "../lib/aggregations";
import { fmtBRL0, fmtNum, fmtPct, CHANNEL_LABEL, CHANNEL_COLOR } from "../lib/format";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, CartesianGrid } from "recharts";
import PageHeader from "../components/ui/PageHeader";
import Card from "../components/ui/Card";

interface FinancialConfig {
  cost_mode: "fixed" | "percentage" | "none";
  cost_fixed_brl: number;
  cost_percentage: number;
  tax_percentage: number;
}

interface SeriesPoint { date: string; meta_spend: number; google_spend: number; tiktok_spend: number; total_spend: number; total_revenue: number; roas: number }

interface SnapshotLite { channel: string; date: string; account_kpis: { spend_brl?: number; impressions?: number; clicks?: number; real_revenue_brl?: number } }

export default function Overview() {
  const [snaps, setSnaps] = useState<SnapshotLite[]>([]);
  const [niara, setNiara] = useState<NiaraDay[]>([]);
  const [config, setConfig] = useState<FinancialConfig>({ cost_mode: "fixed", cost_fixed_brl: 0, cost_percentage: 0, tax_percentage: 0 });
  const [loading, setLoading] = useState(true);
  const [showConfig, setShowConfig] = useState(false);

  useEffect(() => {
    const load = async () => {
      const since = new Date(Date.now() - 30 * 86400_000).toISOString().slice(0, 10);
      const [snapsRes, niaraRes, confRes] = await Promise.all([
        supabase.from("ads_daily_snapshots").select("channel,date,account_kpis").gte("date", since).order("date", { ascending: true }),
        supabase.from("v_niara_daily").select("*").gte("date", since).order("date", { ascending: true }),
        supabase.from("financial_config").select("*").eq("id", "current").single(),
      ]);
      setSnaps((snapsRes.data as SnapshotLite[]) ?? []);
      setNiara((niaraRes.data as NiaraDay[]) ?? []);
      if (confRes.data) {
        const d = confRes.data as { cost_mode: string; cost_fixed_brl: string; cost_percentage: string; tax_percentage: string };
        setConfig({
          cost_mode: d.cost_mode as FinancialConfig["cost_mode"],
          cost_fixed_brl: Number(d.cost_fixed_brl) || 0,
          cost_percentage: Number(d.cost_percentage) || 0,
          tax_percentage: Number(d.tax_percentage) || 0,
        });
      }
      setLoading(false);
    };
    load();
    const ch = supabase.channel("financial_config_changes").on("postgres_changes", { event: "UPDATE", schema: "public", table: "financial_config" }, load).subscribe();
    return () => { ch.unsubscribe(); };
  }, []);

  // Calcula custos operacionais por janela
  function operationalCost(reservations: number, revenue: number): number {
    if (config.cost_mode === "fixed") return reservations * config.cost_fixed_brl;
    if (config.cost_mode === "percentage") return revenue * (config.cost_percentage / 100);
    return 0;
  }
  function taxCost(revenue: number): number {
    return revenue * (config.tax_percentage / 100);
  }

  const byDate = new Map<string, SeriesPoint>();
  for (const s of snaps) {
    const k = s.account_kpis;
    const slot = byDate.get(s.date) ?? { date: s.date, meta_spend: 0, google_spend: 0, tiktok_spend: 0, total_spend: 0, total_revenue: 0, roas: 0 };
    if (s.channel === "meta") slot.meta_spend = k.spend_brl ?? 0;
    if (s.channel === "google") slot.google_spend = k.spend_brl ?? 0;
    if (s.channel === "tiktok") slot.tiktok_spend = k.spend_brl ?? 0;
    slot.total_spend = slot.meta_spend + slot.google_spend + slot.tiktok_spend;
    slot.total_revenue = (slot.total_revenue || 0) + (k.real_revenue_brl ?? 0);
    slot.roas = slot.total_spend > 0 ? slot.total_revenue / slot.total_spend : 0;
    byDate.set(s.date, slot);
  }
  const series = [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));

  function enrich(d: { spend: number; revenue: number; purchases: number; roas: number; days: number }) {
    const opCost = operationalCost(d.purchases, d.revenue);
    const tax = taxCost(d.revenue);
    const totalCost = d.spend + opCost + tax;
    const netRevenue = d.revenue - tax;
    const profit = netRevenue - d.spend - opCost;
    const margin = d.revenue > 0 ? (profit / d.revenue) * 100 : 0;
    const roi = totalCost > 0 ? (profit / totalCost) * 100 : 0;
    return { ...d, netRevenue, opCost, tax, totalCost, profit, margin, roi };
  }

  // KPIs por janela: agrega últimos N dias usando o set de datas disponíveis.
  // IMPORTANTE: receita = receita TOTAL Niara nos dias com gasto > 0 (assume
  // que toda reserva Niara online é atribuível ao share-of-spend dos canais).
  // Isso é um proxy — NÃO é tracking direto. Reservas hoje NÃO carregam
  // utm_source/gclid/affiliation no raw, então não dá pra fazer atribuição real.
  const windows = useMemo(() => {
    const datesDesc = [...new Set(snaps.map((s) => s.date))].sort((a, b) => b.localeCompare(a));
    const niaraByDate = new Map(niara.map((n) => [n.date, n]));
    const calc = (n: number) => {
      const dates = datesDesc.slice(0, n);
      let spend = 0, revenue = 0, purchases = 0;
      for (const d of dates) {
        const slot = byDate.get(d);
        if (slot) spend += slot.total_spend;
        const nr = niaraByDate.get(d);
        if (nr) { revenue += nr.revenue_brl; purchases += nr.reservations; }
      }
      return { spend, revenue, purchases, roas: spend > 0 ? revenue / spend : 0, days: dates.length };
    };
    return { d1: calc(1), d3: calc(3), d7: calc(7), d15: calc(15), d30: calc(30) };
  }, [snaps, niara, byDate]);

  return (
    <>
      <PageHeader
        title="Visão geral"
        subtitle="Últimos 30 dias · todos os canais"
        icon={<LayoutDashboard size={22} strokeWidth={2} />}
        actions={
          <button
            onClick={() => setShowConfig(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white border border-esm-gray-200 hover:border-esm-brown-300 text-esm-gray-700 text-xs font-bold transition-colors"
            title="Configurar custos e impostos"
          >
            <Settings2 size={13} /> Custos
          </button>
        }
      />

      {showConfig && <FinancialConfigModal current={config} onClose={() => setShowConfig(false)} />}

      {/* KPIs multi-janela (24h / 3D / 7D / 15D / 30D) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-4">
        <WindowKpi label="Últimas 24h"     data={enrich(windows.d1)}  tone="primary" />
        <WindowKpi label="Últimos 3 dias"  data={enrich(windows.d3)}  tone="primary2" />
        <WindowKpi label="Últimos 7 dias"  data={enrich(windows.d7)}  tone="secondary" />
        <WindowKpi label="Últimos 15 dias" data={enrich(windows.d15)} tone="secondary" />
        <WindowKpi label="Últimos 30 dias" data={enrich(windows.d30)} tone="tertiary" />
      </div>

      {/* Nota explicativa sobre atribuição */}
      <div className="mb-6 flex items-start gap-3 bg-esm-cream-50 border border-esm-brown-100 rounded-2xl p-4 text-[12px] text-esm-brown-900">
        <AlertCircle size={16} className="text-esm-brown-700 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-bold mb-0.5">Como a receita é calculada</p>
          <p className="leading-relaxed">
            A "receita real" mostrada aqui é a <b>receita total do motor Niara</b> (todas as reservas online do hotel), distribuída entre os canais proporcional ao gasto do dia.
            Isso é um <b>proxy</b>, não atribuição direta. As reservas do Niara não carregam <code>utm_source</code> nem <code>gclid</code> hoje,
            então não dá pra saber qual reserva veio de qual ad. Pra ter atribuição real, precisaríamos passar UTMs pro motor (via GTM).
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <Card title="Gasto diário por canal" subtitle="Últimos 30 dias">
          <div className="h-72">
            {loading ? <Skel /> : (
              <ResponsiveContainer>
                <BarChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E3DB" />
                  <XAxis dataKey="date" fontSize={10} stroke="#9B8E7D" />
                  <YAxis fontSize={10} stroke="#9B8E7D" tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                  <Tooltip formatter={(v: number) => fmtBRL0(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E8E3DB", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="meta_spend" name={CHANNEL_LABEL.meta} stackId="a" fill={CHANNEL_COLOR.meta} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="google_spend" name={CHANNEL_LABEL.google} stackId="a" fill={CHANNEL_COLOR.google} />
                  <Bar dataKey="tiktok_spend" name={CHANNEL_LABEL.tiktok} stackId="a" fill={CHANNEL_COLOR.tiktok} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card title="ROAS real diário" subtitle="Receita motor Niara ÷ gasto total">
          <div className="h-72">
            {loading ? <Skel /> : (
              <ResponsiveContainer>
                <LineChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E3DB" />
                  <XAxis dataKey="date" fontSize={10} stroke="#9B8E7D" />
                  <YAxis fontSize={10} stroke="#9B8E7D" />
                  <Tooltip formatter={(v: number) => v.toFixed(2)} contentStyle={{ borderRadius: 12, border: "1px solid #E8E3DB", fontSize: 12 }} />
                  <Line type="monotone" dataKey="roas" stroke="#966E4D" strokeWidth={2.5} dot={{ r: 3, fill: "#966E4D" }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>

      <ChannelTable snaps={snaps} niara={niara} />
    </>
  );
}

function Skel() { return <div className="h-full w-full bg-esm-cream-50 rounded animate-pulse" />; }

interface WindowData { spend: number; revenue: number; netRevenue: number; opCost: number; tax: number; totalCost: number; profit: number; margin: number; roi: number; purchases: number; roas: number; days: number }
function WindowKpi({ label, data, tone }: { label: string; data: WindowData; tone: "primary" | "primary2" | "secondary" | "tertiary" }) {
  const cfg: Record<string, { container: string; valueColor: string; labelColor: string; subColor: string; positiveColor: string; warningColor: string; negativeColor: string }> = {
    primary:   { container: "bg-gradient-to-br from-esm-brown-700 to-esm-brown-900 text-white shadow-card-hover", valueColor: "text-white",       labelColor: "text-esm-cream-100/80", subColor: "text-esm-cream-100/70", positiveColor: "text-emerald-300", warningColor: "text-amber-300", negativeColor: "text-red-300" },
    primary2:  { container: "bg-esm-brown-500 text-white shadow-card-hover",                                      valueColor: "text-white",       labelColor: "text-esm-cream-100/85", subColor: "text-esm-cream-100/75", positiveColor: "text-emerald-200", warningColor: "text-amber-200", negativeColor: "text-red-200" },
    secondary: { container: "bg-white border border-esm-gray-100 shadow-card",                                    valueColor: "text-esm-gray-900", labelColor: "text-esm-gray-400",     subColor: "text-esm-gray-500",      positiveColor: "text-emerald-700", warningColor: "text-amber-500", negativeColor: "text-red-600" },
    tertiary:  { container: "bg-esm-cream-50 border border-esm-brown-100 shadow-card",                            valueColor: "text-esm-gray-900", labelColor: "text-esm-brown-700",    subColor: "text-esm-brown-700/70",  positiveColor: "text-emerald-700", warningColor: "text-amber-500", negativeColor: "text-red-600" },
  };
  const s = cfg[tone]!;
  // Cor do Lucro pela ROAS da janela: ≥5 verde · 2–4,99 âmbar · <2 vermelho.
  const profitColor = data.roas >= 5 ? s.positiveColor : data.roas >= 2 ? s.warningColor : s.negativeColor;
  return (
    <div className={`rounded-2xl p-5 ${s.container}`}>
      <div className="flex items-center justify-between mb-4">
        <p className={`text-[11px] font-bold uppercase tracking-wider ${s.labelColor}`}>{label}</p>
        <p className={`text-[10px] ${s.subColor}`}>{data.days} dia{data.days === 1 ? "" : "s"}</p>
      </div>
      <div className="space-y-3">
        <div className="flex items-baseline justify-between gap-3">
          <div className="flex-1">
            <p className={`text-[10px] uppercase tracking-wider font-bold ${s.labelColor}`}>Receita</p>
            <p className={`font-display text-xl tabular-nums ${s.valueColor} leading-tight mt-0.5`}>{fmtBRL0(data.revenue)}</p>
            <p className={`text-[10px] ${s.subColor} mt-0.5`}>{fmtNum(data.purchases)} compras</p>
          </div>
          <div className="text-right">
            <p className={`text-[10px] uppercase tracking-wider font-bold ${s.labelColor}`}>Gasto</p>
            <p className={`font-display text-xl tabular-nums ${s.valueColor} leading-tight mt-0.5`}>{fmtBRL0(data.spend)}</p>
          </div>
        </div>
        <div className="border-t border-current/10 pt-3">
          <div className="flex items-baseline justify-between gap-3">
            <p className={`text-[10px] uppercase tracking-wider font-bold ${s.labelColor}`}>Lucro</p>
            <p className={`font-display text-[28px] tabular-nums ${profitColor} leading-tight`}>{fmtBRL0(data.profit)}</p>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2 text-center">
            <Mini label="ROAS"   value={data.roas > 0 ? `${data.roas.toFixed(2).replace(".", ",")}x` : "—"}                         tone={s} />
            <Mini label="ROI"    value={data.totalCost > 0 ? `${data.roi.toFixed(0)}%` : "—"}                                       tone={s} />
            <Mini label="Margem" value={data.revenue > 0 ? `${data.margin.toFixed(0)}%` : "—"}                                      tone={s} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Mini({ label, value, tone }: { label: string; value: string; tone: { labelColor: string; valueColor: string; subColor: string } }) {
  return (
    <div className="bg-black/5 dark:bg-white/5 rounded-md py-1.5">
      <p className={`text-[9px] uppercase tracking-wider font-bold ${tone.labelColor}`}>{label}</p>
      <p className={`text-xs font-bold tabular-nums ${tone.valueColor} mt-0.5`}>{value}</p>
    </div>
  );
}

function FinancialConfigModal({ current, onClose }: { current: FinancialConfig; onClose: () => void }) {
  const [mode, setMode] = useState(current.cost_mode);
  const [fixed, setFixed] = useState(current.cost_fixed_brl);
  const [pct, setPct] = useState(current.cost_percentage);
  const [tax, setTax] = useState(current.tax_percentage);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await supabase.from("financial_config").update({
      cost_mode: mode,
      cost_fixed_brl: fixed,
      cost_percentage: pct,
      tax_percentage: tax,
      updated_at: new Date().toISOString(),
    }).eq("id", "current");
    setSaving(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <header className="px-5 py-4 border-b border-esm-gray-100 flex items-center justify-between">
          <h2 className="text-sm font-bold text-esm-gray-900">Configurar custos</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-esm-gray-100 text-esm-gray-400">
            <X size={16} />
          </button>
        </header>
        <div className="p-5 space-y-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-esm-gray-400 mb-2">Custo operacional por reserva</p>
            <div className="flex gap-1 bg-esm-cream-50 rounded-lg p-0.5 mb-2">
              {([
                { v: "fixed" as const, l: "Valor fixo" },
                { v: "percentage" as const, l: "% do ticket" },
                { v: "none" as const, l: "Sem custo" },
              ]).map((o) => (
                <button
                  key={o.v}
                  onClick={() => setMode(o.v)}
                  className={`flex-1 px-2.5 py-1.5 text-xs font-bold rounded transition-colors ${mode === o.v ? "bg-white text-esm-brown-700 shadow-sm" : "text-esm-gray-600 hover:text-esm-gray-900"}`}
                >{o.l}</button>
              ))}
            </div>
            {mode === "fixed" && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-esm-gray-500">R$</span>
                <input
                  type="number" step="0.01" min="0"
                  value={fixed}
                  onChange={(e) => setFixed(Number(e.target.value))}
                  className="flex-1 px-3 py-2 text-sm border border-esm-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-esm-brown-500/30"
                />
                <span className="text-xs text-esm-gray-500">por reserva</span>
              </div>
            )}
            {mode === "percentage" && (
              <div className="flex items-center gap-2">
                <input
                  type="number" step="0.1" min="0" max="100"
                  value={pct}
                  onChange={(e) => setPct(Number(e.target.value))}
                  className="flex-1 px-3 py-2 text-sm border border-esm-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-esm-brown-500/30"
                />
                <span className="text-xs text-esm-gray-500">% do ticket</span>
              </div>
            )}
            <p className="text-[11px] text-esm-gray-500 mt-1.5">
              Ex: enxoval, café da manhã, comissão recepção, depreciação. Use o que faz sentido pra você.
            </p>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-esm-gray-400 mb-2">Impostos sobre receita</p>
            <div className="flex items-center gap-2">
              <input
                type="number" step="0.1" min="0" max="100"
                value={tax}
                onChange={(e) => setTax(Number(e.target.value))}
                className="flex-1 px-3 py-2 text-sm border border-esm-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-esm-brown-500/30"
              />
              <span className="text-xs text-esm-gray-500">% da receita</span>
            </div>
            <p className="text-[11px] text-esm-gray-500 mt-1.5">
              ISS, COFINS, PIS. Aplicado sobre a receita bruta pra calcular receita líquida.
            </p>
          </div>

          <div className="bg-esm-cream-50 border border-esm-brown-100 rounded-xl p-3">
            <p className="text-[11px] font-bold text-esm-brown-900 mb-1">Fórmula resultante</p>
            <p className="text-[11px] text-esm-brown-900 leading-relaxed font-mono">
              Lucro = Receita − Impostos − Gasto mídia − Custo operacional<br/>
              Margem = Lucro / Receita<br/>
              ROI = Lucro / (Mídia + Custo op + Impostos)
            </p>
          </div>
        </div>
        <footer className="px-5 py-3 border-t border-esm-gray-100 flex gap-2 justify-end">
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-bold text-esm-gray-700 hover:bg-esm-gray-50 rounded-lg transition-colors">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-esm-brown-500 hover:bg-esm-brown-700 text-white rounded-lg transition-colors disabled:opacity-50">
            <Save size={12} /> {saving ? "Salvando…" : "Salvar"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function ChannelTable({ snaps, niara }: { snaps: SnapshotLite[]; niara: NiaraDay[] }) {
  // Reservations e revenue por canal: share-of-spend × Niara real por dia.
  const niaraByDate = new Map(niara.map((n) => [n.date, n]));
  const spendByDate = new Map<string, number>();
  for (const s of snaps) spendByDate.set(s.date, (spendByDate.get(s.date) ?? 0) + (s.account_kpis.spend_brl || 0));

  const map = new Map<string, { spend: number; impressions: number; clicks: number; reservations: number; revenue: number }>();
  for (const s of snaps) {
    const k = s.account_kpis;
    const slot = map.get(s.channel) ?? { spend: 0, impressions: 0, clicks: 0, reservations: 0, revenue: 0 };
    slot.spend += k.spend_brl ?? 0;
    slot.impressions += k.impressions ?? 0;
    slot.clicks += k.clicks ?? 0;
    const totalDay = spendByDate.get(s.date) ?? 0;
    const share = totalDay > 0 ? (k.spend_brl ?? 0) / totalDay : 0;
    const n = niaraByDate.get(s.date);
    if (n) {
      slot.reservations += n.reservations * share;
      slot.revenue += n.revenue_brl * share;
    }
    map.set(s.channel, slot);
  }
  const rows = [...map.entries()].map(([channel, v]) => ({
    channel,
    ...v,
    ctr: v.impressions > 0 ? (v.clicks / v.impressions) * 100 : 0,
    cpm: v.impressions > 0 ? (v.spend / v.impressions) * 1000 : 0,
    cpa: v.reservations > 0 ? v.spend / v.reservations : 0,
    roas: v.spend > 0 ? v.revenue / v.spend : 0,
  })).sort((a, b) => b.spend - a.spend);

  return (
    <Card title="Resumo por canal" subtitle="Últimos 30 dias" padded={false}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-esm-cream-50">
            <tr className="text-left text-esm-gray-500 text-[11px] uppercase tracking-wider">
              <th className="py-3 px-5">Canal</th>
              <th className="py-3 px-5 text-right">Gasto</th>
              <th className="py-3 px-5 text-right">Impressões</th>
              <th className="py-3 px-5 text-right">Clicks</th>
              <th className="py-3 px-5 text-right">CTR</th>
              <th className="py-3 px-5 text-right">CPM</th>
              <th className="py-3 px-5 text-right">Compras (atrib.)</th>
              <th className="py-3 px-5 text-right">CPA</th>
              <th className="py-3 px-5 text-right">Receita real</th>
              <th className="py-3 px-5 text-right">ROAS real</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.channel} className="border-t border-esm-gray-100">
                <td className="py-3 px-5 font-bold text-esm-gray-900 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: CHANNEL_COLOR[r.channel] }} />
                  {CHANNEL_LABEL[r.channel]}
                </td>
                <td className="py-3 px-5 text-right tabular-nums">{fmtBRL0(r.spend)}</td>
                <td className="py-3 px-5 text-right tabular-nums text-esm-gray-700">{fmtNum(r.impressions)}</td>
                <td className="py-3 px-5 text-right tabular-nums text-esm-gray-700">{fmtNum(r.clicks)}</td>
                <td className="py-3 px-5 text-right tabular-nums text-esm-gray-700">{fmtPct(r.ctr, 2)}</td>
                <td className="py-3 px-5 text-right tabular-nums text-esm-gray-700">{fmtBRL0(r.cpm)}</td>
                <td className="py-3 px-5 text-right tabular-nums">{r.reservations >= 0.1 ? r.reservations.toFixed(1).replace(".", ",") : "—"}</td>
                <td className="py-3 px-5 text-right tabular-nums">{r.cpa > 0 ? fmtBRL0(r.cpa) : "—"}</td>
                <td className="py-3 px-5 text-right tabular-nums">{fmtBRL0(r.revenue)}</td>
                <td className="py-3 px-5 text-right tabular-nums font-bold text-esm-brown-700">{r.roas.toFixed(2)}x</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={10} className="py-10 text-center text-esm-gray-400 italic">Sem dados ainda.</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
